import type { EntityPermission, User, UserGroup } from '@gmn-portal/backend';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  type EntityPermissionOverviewEntry,
  payloadClient,
  useCreateEntityPermission,
  useDeleteEntityPermission,
  useEntityPermissionOverview,
  useEntityPermissions,
  useUpdateEntityPermission,
  useUserGroups,
  useUsers,
  type Where,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PaginationNavigation } from '@/shared/components/pagination';
import { TableSearchBar } from '@/shared/components/tables';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import {
  type AclServiceKey,
  canReadService,
  getServiceScopes,
  hasServicePermission,
  isCustomerAdminGroup,
  isGmnAdminGroup,
  type ScopeOption,
  serviceRegistry,
} from '@/shared/utils/permissions';
import type { PermissionCustomerContext, PermissionCustomerContextSource } from '../types';
import { UsersAccessTable } from './users-access-table';

type EntityPermissionsTabProps = {
  entityId: string;
  entityType: AclServiceKey;
  customerContexts?: PermissionCustomerContext[];
};

const resolveCustomerId = (value?: string | { id: string } | null) => {
  if (!value) return undefined;
  return typeof value === 'string' ? value : value.id;
};

const getUserId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value && typeof (value as any).id === 'string') {
    return (value as any).id;
  }
  return null;
};

const getUserLabel = (user: User) => {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName !== '' ? fullName : user.email || user.id;
};

type ResolvedCustomerContext = {
  id: string;
  customerId: string;
  source: PermissionCustomerContextSource;
};

type CustomerContextUsersPage = {
  docs: User[];
  totalPages: number;
  totalDocs: number;
};

type UserSection = {
  id: string;
  title: string;
  users: User[];
  totalPages: number;
  totalItems: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  emptyLabel?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

const getCustomerContextId = (context: PermissionCustomerContext) =>
  `customer-${context.customerId}-${context.source}`;

export function EntityPermissionsTab({
  entityId,
  entityType,
  customerContexts,
}: EntityPermissionsTabProps) {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();

  const isGmnAdmin = isGmnAdminGroup(currentUser);
  const isCustomerAdmin = isCustomerAdminGroup(currentUser);
  const isAuthorized = isGmnAdmin || isCustomerAdmin;
  const isCustomerView = isCustomerAdmin && !isGmnAdmin;

  const resolvedCustomerContexts = useMemo<ResolvedCustomerContext[]>(() => {
    const fallbackCustomerId = resolveCustomerId(
      currentUser?.customer as string | { id: string } | null | undefined
    );

    const contexts = (customerContexts ?? [])
      .map((context) => ({
        customerId: resolveCustomerId(context.customerId),
        source: context.source,
      }))
      .filter(
        (
          context
        ): context is {
          customerId: string;
          source: PermissionCustomerContextSource;
        } => Boolean(context.customerId)
      );

    const mergedByCustomerId = new Map<string, PermissionCustomerContextSource>();
    for (const context of contexts) {
      const existing = mergedByCustomerId.get(context.customerId);
      if (!existing) {
        mergedByCustomerId.set(context.customerId, context.source);
        continue;
      }

      if (existing !== context.source) {
        mergedByCustomerId.set(context.customerId, 'combined');
      }
    }

    if (mergedByCustomerId.size > 0) {
      return Array.from(mergedByCustomerId.entries()).map(([customerId, source]) => ({
        id: getCustomerContextId({ customerId, source }),
        customerId,
        source,
      }));
    }

    if (!fallbackCustomerId) return [];
    return [
      {
        id: getCustomerContextId({
          customerId: fallbackCustomerId,
          source: 'assigned-customer',
        }),
        customerId: fallbackCustomerId,
        source: 'assigned-customer',
      },
    ];
  }, [currentUser?.customer, customerContexts]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [gmnPage, setGmnPage] = useState(1);
  const [gmnPageSize, setGmnPageSize] = useState(10);
  const [customerPagesByContextId, setCustomerPagesByContextId] = useState<Record<string, number>>(
    {}
  );
  const [customerPageSizesByContextId, setCustomerPageSizesByContextId] = useState<
    Record<string, number>
  >({});
  const [activeSectionId, setActiveSectionId] = useState<string>('gmn');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: userGroupsData } = useUserGroups({
    limit: 0,
    sort: 'key',
    disabled: !isAuthorized,
  });
  const userGroups = userGroupsData?.docs || [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group])),
    [userGroups]
  );
  const gmnGroupIds = userGroups.filter((group) => group.domain === 'gmn').map((group) => group.id);
  const customerGroupIds = userGroups
    .filter((group) => group.domain === 'customer')
    .map((group) => group.id);

  const searchFilter = useMemo<Where | null>(() => {
    const value = debouncedSearch.trim();
    if (!value) return null;
    const conditions: Where[] = [
      { email: { like: value } },
      { firstName: { like: value } },
      { lastName: { like: value } },
    ];
    return {
      or: conditions,
    };
  }, [debouncedSearch]);

  const gmnWhere = useMemo(() => {
    if (!isGmnAdmin || gmnGroupIds.length === 0) return undefined;
    const and: Where[] = [
      { userGroup: { in: gmnGroupIds } },
      { customer: { exists: false } },
      { archived: { not_equals: true } },
    ];
    if (searchFilter) {
      and.push(searchFilter);
    }
    return { and };
  }, [gmnGroupIds, isGmnAdmin, searchFilter]);

  const {
    data: gmnUsersData,
    isLoading: isLoadingGmnUsers,
    isFetching: isFetchingGmnUsers,
  } = useUsers({
    depth: 0,
    page: gmnPage,
    limit: gmnPageSize,
    sort: 'lastName',
    where: gmnWhere,
    disabled: !isAuthorized || !isGmnAdmin || !gmnWhere,
  });

  const customerWhereByContextId = useMemo(() => {
    const whereByContextId = new Map<string, Where>();
    for (const context of resolvedCustomerContexts) {
      const and: Where[] = [
        { userGroup: { in: customerGroupIds } },
        { customer: { equals: context.customerId } },
        { archived: { not_equals: true } },
      ];
      if (searchFilter) {
        and.push(searchFilter);
      }
      whereByContextId.set(context.id, { and });
    }
    return whereByContextId;
  }, [customerGroupIds, resolvedCustomerContexts, searchFilter]);

  const customerQueryParams = useMemo(
    () =>
      resolvedCustomerContexts.map((context) => ({
        context,
        page: customerPagesByContextId[context.id] ?? 1,
        pageSize: customerPageSizesByContextId[context.id] ?? 10,
        where: customerWhereByContextId.get(context.id),
      })),
    [
      customerPagesByContextId,
      customerPageSizesByContextId,
      customerWhereByContextId,
      resolvedCustomerContexts,
    ]
  );

  const {
    data: customerUsersByContextData,
    isLoading: isLoadingCustomerUsersByContext,
    isFetching: isFetchingCustomerUsersByContext,
  } = useQuery({
    queryKey: ['users', 'permissions-by-customer-context', customerQueryParams],
    enabled:
      isAuthorized &&
      customerGroupIds.length > 0 &&
      customerQueryParams.length > 0 &&
      customerQueryParams.every((param) => Boolean(param.where)),
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      return Promise.all(
        customerQueryParams.map(async (param) => {
          const response = await payloadClient.find({
            collection: 'users',
            depth: 0,
            page: param.page,
            limit: param.pageSize,
            sort: 'lastName',
            where: param.where,
          });
          return { contextId: param.context.id, response };
        })
      );
    },
  });

  useEffect(() => {
    setGmnPage(1);
    setCustomerPagesByContextId(() => {
      const next: Record<string, number> = {};
      resolvedCustomerContexts.forEach((context) => {
        next[context.id] = 1;
      });
      return next;
    });
  }, [debouncedSearch, resolvedCustomerContexts]);

  useEffect(() => {
    if (gmnUsersData && gmnPage > gmnUsersData.totalPages) {
      setGmnPage(Math.max(1, gmnUsersData.totalPages));
    }
  }, [gmnPage, gmnUsersData]);

  useEffect(() => {
    if (!customerUsersByContextData || customerUsersByContextData.length === 0) return;
    setCustomerPagesByContextId((previous) => {
      let changed = false;
      const next = { ...previous };

      for (const entry of customerUsersByContextData) {
        const currentPage = previous[entry.contextId] ?? 1;
        const totalPages = Math.max(1, entry.response.totalPages ?? 1);
        if (currentPage > totalPages) {
          next[entry.contextId] = totalPages;
          changed = true;
        }
      }

      return changed ? next : previous;
    });
  }, [customerUsersByContextData]);

  const customerUsersByContextId = useMemo(() => {
    const map = new Map<string, CustomerContextUsersPage>();
    for (const entry of customerUsersByContextData ?? []) {
      map.set(entry.contextId, {
        docs: (entry.response.docs || []) as User[],
        totalPages: entry.response.totalPages ?? 0,
        totalDocs: entry.response.totalDocs ?? 0,
      });
    }
    return map;
  }, [customerUsersByContextData]);

  const resolveCustomerSectionTitle = (source: PermissionCustomerContextSource) => {
    switch (source) {
      case 'assigned-customer':
        return t('permissions.customerSections.assignedCustomer');
      case 'assigned-helicopter-service-provider':
        return t('permissions.customerSections.helicopterServiceProvider');
      case 'combined':
        return t('permissions.customerSections.combined');
      default:
        return t('permissions.customerUsers');
    }
  };

  const gmnSection = useMemo<UserSection | null>(() => {
    if (!isGmnAdmin) return null;
    return {
      id: 'gmn',
      title: t('permissions.gmnUsers'),
      users: (gmnUsersData?.docs || []) as User[],
      totalPages: gmnUsersData?.totalPages ?? 0,
      totalItems: gmnUsersData?.totalDocs ?? 0,
      page: gmnPage,
      pageSize: gmnPageSize,
      isLoading: isLoadingGmnUsers || isFetchingGmnUsers,
      onPageChange: setGmnPage,
      onPageSizeChange: setGmnPageSize,
    };
  }, [
    gmnPage,
    gmnPageSize,
    gmnUsersData?.docs,
    gmnUsersData?.totalDocs,
    gmnUsersData?.totalPages,
    isFetchingGmnUsers,
    isGmnAdmin,
    isLoadingGmnUsers,
    t,
  ]);

  const customerSections = useMemo<UserSection[]>(() => {
    return resolvedCustomerContexts.map((context) => {
      const page = customerPagesByContextId[context.id] ?? 1;
      const pageSize = customerPageSizesByContextId[context.id] ?? 10;
      const response = customerUsersByContextId.get(context.id);
      return {
        id: context.id,
        title: resolveCustomerSectionTitle(context.source),
        users: response?.docs || [],
        totalPages: response?.totalPages ?? 0,
        totalItems: response?.totalDocs ?? 0,
        page,
        pageSize,
        isLoading: isLoadingCustomerUsersByContext || isFetchingCustomerUsersByContext,
        emptyLabel: t('permissions.noUsersForSection'),
        onPageChange: (nextPage) => {
          setCustomerPagesByContextId((previous) => ({ ...previous, [context.id]: nextPage }));
        },
        onPageSizeChange: (nextPageSize) => {
          setCustomerPageSizesByContextId((previous) => ({
            ...previous,
            [context.id]: nextPageSize,
          }));
          setCustomerPagesByContextId((previous) => ({ ...previous, [context.id]: 1 }));
        },
      };
    });
  }, [
    customerPageSizesByContextId,
    customerPagesByContextId,
    customerUsersByContextId,
    isFetchingCustomerUsersByContext,
    isLoadingCustomerUsersByContext,
    resolvedCustomerContexts,
    t,
  ]);

  const userSections = useMemo(() => {
    return [...(gmnSection ? [gmnSection] : []), ...customerSections];
  }, [customerSections, gmnSection]);

  useEffect(() => {
    if (userSections.length === 0) {
      setActiveSectionId('');
      return;
    }
    if (!userSections.some((section) => section.id === activeSectionId)) {
      setActiveSectionId(userSections[0].id);
    }
  }, [activeSectionId, userSections]);

  const allVisibleUsers = useMemo(() => {
    const byId = new Map<string, User>();
    for (const section of userSections) {
      for (const user of section.users) {
        byId.set(user.id, user);
      }
    }
    return Array.from(byId.values());
  }, [userSections]);

  const overviewUserIds = useMemo(() => {
    return allVisibleUsers.map((user) => user.id);
  }, [allVisibleUsers]);

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isFetching: isFetchingOverview,
  } = useEntityPermissionOverview({
    entityType,
    entityId,
    userIds: overviewUserIds,
    disabled: !isAuthorized || overviewUserIds.length === 0,
  });

  const hasOverview = Boolean(overviewData);

  const overviewByUserId = useMemo(() => {
    const map = new Map<string, EntityPermissionOverviewEntry>();
    for (const entry of overviewData?.entries ?? []) {
      map.set(entry.userId, entry);
    }
    return map;
  }, [overviewData?.entries]);

  const { data: permissionsData } = useEntityPermissions({
    limit: 0,
    depth: 0,
    where:
      overviewUserIds.length > 0
        ? {
            and: [
              { entityType: { equals: entityType } },
              { entityId: { equals: entityId } },
              { user: { in: overviewUserIds } },
            ],
          }
        : undefined,
    disabled: !isAuthorized || overviewUserIds.length === 0,
  });

  const permissionEntries = (permissionsData?.docs || []) as EntityPermission[];
  const permissionByUserId = useMemo(() => {
    const map = new Map<string, EntityPermission>();
    permissionEntries.forEach((entry) => {
      const userId = getUserId(entry.user);
      if (userId) map.set(userId, entry);
    });
    return map;
  }, [permissionEntries]);

  const createPermission = useCreateEntityPermission();
  const updatePermission = useUpdateEntityPermission();
  const deletePermission = useDeleteEntityPermission();
  const isSaving =
    createPermission.isPending || updatePermission.isPending || deletePermission.isPending;

  const resolveGroupLabel = (group: UserGroup | null) => {
    const labelKey = `portalRoles.${group?.key ?? ''}`;
    const translated = group?.key ? t(labelKey as unknown as never) : '';
    return group?.key && translated !== labelKey ? translated : (group?.key ?? '-');
  };

  const resolveGroup = (user: User): UserGroup | null => {
    const group = user.userGroup;
    if (!group) return null;
    if (typeof group === 'string') {
      return userGroupsById.get(group) ?? null;
    }
    return group as UserGroup;
  };

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return allVisibleUsers.find((user) => user.id === selectedUserId) ?? null;
  }, [allVisibleUsers, selectedUserId]);

  const selectedReadId = selectedUser
    ? `entity-permissions-read-${selectedUser.id}`
    : 'entity-permissions-read';
  const selectedUpdateId = selectedUser
    ? `entity-permissions-update-${selectedUser.id}`
    : 'entity-permissions-update';

  const selectedGroup = selectedUser ? resolveGroup(selectedUser) : null;
  const selectedGroupLabel = selectedGroup ? resolveGroupLabel(selectedGroup) : '-';
  const selectedGroupLink = selectedGroup?.key
    ? `/gmn/settings/user-groups?group=${encodeURIComponent(selectedGroup.key)}`
    : null;
  const selectedPermission = selectedUser ? permissionByUserId.get(selectedUser.id) : null;
  const selectedOverview = selectedUser ? overviewByUserId.get(selectedUser.id) : null;
  const selectedReadAccess = selectedOverview?.read ?? { allowed: false, sources: [] };
  const selectedUpdateAccess = selectedOverview?.update ?? { allowed: false, sources: [] };
  const selectedScopes = selectedGroup ? getServiceScopes(selectedGroup, entityType) : [];
  const selectedHasReadPermission = selectedGroup
    ? hasServicePermission(selectedGroup, entityType, 'read')
    : false;
  const selectedHasUpdatePermission = selectedGroup
    ? hasServicePermission(selectedGroup, entityType, 'update')
    : false;
  const selectedSupportsExplicitApproval = selectedScopes.includes('explicit-approval');
  const selectedCanRead =
    selectedSupportsExplicitApproval && canReadService(selectedGroup, entityType);
  const selectedCanUpdate =
    selectedSupportsExplicitApproval &&
    selectedHasUpdatePermission &&
    canReadService(selectedGroup, entityType);
  const selectedReadChecked = Boolean(selectedPermission?.read);
  const selectedUpdateChecked = Boolean(selectedPermission?.update);
  const selectedExplicitReadMessage = !selectedSupportsExplicitApproval
    ? t('permissions.explicitNotApplicable')
    : !selectedHasReadPermission
      ? t('permissions.missingReadPermission')
      : null;
  const selectedExplicitUpdateMessage = !selectedSupportsExplicitApproval
    ? t('permissions.explicitNotApplicable')
    : !selectedHasReadPermission
      ? t('permissions.missingReadPermission')
      : !selectedHasUpdatePermission
        ? t('permissions.missingUpdatePermission')
        : null;
  const renderSourceBadges = (sources: ScopeOption[]) => {
    if (sources.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">{t('permissions.noAccessSource')}</span>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        {sources.map((source) => (
          <Badge key={source} variant="outline-solid">
            {t(`permissions.scopes.${source}` as unknown as never)}
          </Badge>
        ))}
      </div>
    );
  };

  const renderScopeBadges = (scopes: ScopeOption[]) => {
    if (scopes.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">{t('permissions.noGroupScopes')}</span>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        {scopes.map((scope) => (
          <Badge key={scope} variant="outline-solid">
            {t(`permissions.scopes.${scope}` as unknown as never)}
          </Badge>
        ))}
      </div>
    );
  };

  const renderAccessBadge = (allowed: boolean | null) => {
    return (
      <Badge variant={allowed === null ? 'secondary' : allowed ? 'outline-solid' : 'secondary'}>
        {allowed === null
          ? t('permissions.accessLoading')
          : allowed
            ? t('permissions.accessGranted')
            : t('permissions.accessDenied')}
      </Badge>
    );
  };

  const serviceLabel = t(serviceRegistry[entityType].labelKey as unknown as never);

  const resolveSourceReason = (source: ScopeOption) => {
    switch (source) {
      case 'all':
        return t('permissions.reasons.sources.all', { service: serviceLabel } as never);
      case 'customer-assignment':
        return t('permissions.reasons.sources.customerAssignment', {
          service: serviceLabel,
        } as never);
      case 'user-assignment':
        return selectedGroup?.domain === 'gmn'
          ? t('permissions.reasons.sources.userAssignmentGmn')
          : t('permissions.reasons.sources.userAssignmentCustomer');
      case 'explicit-approval':
        return t('permissions.reasons.sources.explicitApproval');
      default:
        return '';
    }
  };

  const buildReasonList = (action: 'read' | 'update') => {
    if (!selectedOverview) {
      return { status: 'loading' as const, reasons: [t('permissions.reasons.loading')] };
    }

    const access = action === 'read' ? selectedReadAccess : selectedUpdateAccess;
    if (access.allowed) {
      const reasons = access.sources
        .map(resolveSourceReason)
        .filter((reason) => reason.trim().length > 0);
      return { status: 'allowed' as const, reasons };
    }

    if (action === 'update') {
      if (!selectedHasUpdatePermission) {
        return {
          status: 'denied' as const,
          reasons: [t('permissions.reasons.denied.updatePermission')],
        };
      }
      if (!selectedHasReadPermission) {
        return {
          status: 'denied' as const,
          reasons: [t('permissions.reasons.denied.readRequired')],
        };
      }
    }

    if (!selectedHasReadPermission) {
      return {
        status: 'denied' as const,
        reasons: [t('permissions.reasons.denied.readPermission')],
      };
    }

    if (selectedScopes.length === 0) {
      return {
        status: 'denied' as const,
        reasons: [t('permissions.reasons.denied.noScope')],
      };
    }

    return {
      status: 'denied' as const,
      reasons: [t('permissions.reasons.denied.noSource')],
    };
  };

  const handleToggle = async (user: User, next: { read: boolean; update: boolean }) => {
    const userId = user.id;
    const existing = permissionByUserId.get(userId);

    try {
      if (!next.read && !next.update) {
        if (existing) {
          await deletePermission.mutateAsync(existing.id);
        }
        return;
      }

      if (existing) {
        await updatePermission.mutateAsync({
          id: existing.id,
          data: { read: next.read, update: next.update },
        });
        return;
      }

      await createPermission.mutateAsync({
        entityType: entityType as EntityPermission['entityType'],
        entityId,
        user: userId,
        read: next.read,
        update: next.update,
      });
    } catch (error) {
      toast.error(t('permissions.updateFailed'));
      throw error;
    }
  };

  const handleSelectedReadChange = async (checked: boolean) => {
    if (!selectedUser) return;
    const next = { read: checked, update: checked ? selectedUpdateChecked : false };
    await handleToggle(selectedUser, next);
  };

  const handleSelectedUpdateChange = async (checked: boolean) => {
    if (!selectedUser) return;
    const next = { read: checked ? true : selectedReadChecked, update: checked };
    await handleToggle(selectedUser, next);
  };

  const renderTable = (section: UserSection) => {
    return (
      <div className="rounded-lg border border-border overflow-hidden">
        <UsersAccessTable
          users={section.users}
          userGroupsById={userGroupsById}
          overviewByUserId={overviewByUserId}
          isOverviewLoading={
            section.isLoading || isOverviewLoading || isFetchingOverview || !hasOverview
          }
          emptyLabel={section.emptyLabel}
          renderAction={({ user, group }) => {
            const entry = permissionByUserId.get(user.id);
            const readChecked = Boolean(entry?.read);
            const updateChecked = Boolean(entry?.update);
            const hasReadPermission = group
              ? hasServicePermission(group, entityType, 'read')
              : false;
            const scopes = group ? getServiceScopes(group, entityType) : [];
            const supportsExplicitApproval = scopes.includes('explicit-approval');
            const canRead = supportsExplicitApproval && canReadService(group, entityType);
            const canEditExplicit = supportsExplicitApproval && hasReadPermission && canRead;
            const actionLabel = canEditExplicit
              ? readChecked || updateChecked
                ? t('permissions.editApproval')
                : t('permissions.grantApproval')
              : t('permissions.viewAccess');

            return (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedUserId(user.id);
                  setDetailsOpen(true);
                }}
                aria-label={actionLabel}
              >
                {actionLabel}
              </Button>
            );
          }}
        />
      </div>
    );
  };

  if (!isAuthorized) {
    return <div className="text-sm text-muted-foreground">{t('permissions.notAuthorized')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
        <TableSearchBar
          searchTerm={search}
          onSearchChange={setSearch}
          placeholder={t('permissions.searchPlaceholder')}
        />
      </div>

      {userSections.length > 0 ? (
        <Tabs value={activeSectionId} onValueChange={setActiveSectionId}>
          <TabsList className="h-auto bg-transparent p-0 gap-2 border-b rounded-none">
            {userSections.map((section) => (
              <TabsTrigger
                key={section.id}
                value={section.id}
                className="rounded-b-none data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
                data-testid={`permissions-user-section-${section.id}`}
              >
                {section.title}
              </TabsTrigger>
            ))}
          </TabsList>
          {userSections.map((section) => (
            <TabsContent key={section.id} value={section.id} className="space-y-3 m-0 pt-4">
              {renderTable(section)}
              {section.totalPages > 1 && (
                <PaginationNavigation
                  currentPage={section.page}
                  totalPages={section.totalPages}
                  pageSize={section.pageSize}
                  totalItems={section.totalItems}
                  onPageChange={section.onPageChange}
                  onPageSizeChange={section.onPageSizeChange}
                  showPageSizeSelector
                />
              )}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <div className="text-sm text-muted-foreground">{t('permissions.assignCustomerHint')}</div>
      )}

      <Dialog
        open={detailsOpen}
        onOpenChange={(open) => {
          setDetailsOpen(open);
          if (!open) {
            setSelectedUserId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('permissions.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {selectedUser
                ? `${getUserLabel(selectedUser)} · ${selectedGroupLabel}`
                : t('permissions.detailsPlaceholder')}
            </DialogDescription>
          </DialogHeader>

          {selectedUser ? (
            <div className="mt-4 space-y-4">
              <Card>
                <CardContent className="space-y-3">
                  <h4 className="text-sm font-semibold">{t('permissions.effectiveAccess')}</h4>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex items-center justify-between rounded-md border border-border p-3">
                      <span className="text-sm">{t('permissions.read')}</span>
                      {renderAccessBadge(selectedOverview ? selectedReadAccess.allowed : null)}
                    </div>
                    <div className="flex items-center justify-between rounded-md border border-border p-3">
                      <span className="text-sm">{t('permissions.update')}</span>
                      {renderAccessBadge(selectedOverview ? selectedUpdateAccess.allowed : null)}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {!isCustomerView && (
                <>
                  <Card>
                    <CardContent className="space-y-3">
                      <h4 className="text-sm font-semibold">{t('permissions.reasonsTitle')}</h4>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.read')}
                          </div>
                          <ul className="list-disc space-y-1 pl-4 text-sm text-muted-foreground">
                            {buildReasonList('read').reasons.map((reason) => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.update')}
                          </div>
                          <ul className="list-disc space-y-1 pl-4 text-sm text-muted-foreground">
                            {buildReasonList('update').reasons.map((reason) => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="space-y-4">
                      <h4 className="text-sm font-semibold">{t('permissions.accessLadder')}</h4>
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <div className="mt-0.5 flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            1
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              {t('permissions.stepGroupPermissions')}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <span>{t('permissions.groupLabel')}</span>
                              <Badge variant="secondary">{selectedGroupLabel}</Badge>
                              {selectedGroupLink && (
                                <a
                                  href={selectedGroupLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline underline-offset-2"
                                >
                                  {t('permissions.viewGroup')}
                                </a>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <Badge
                                variant={selectedHasReadPermission ? 'outline-solid' : 'secondary'}
                              >
                                {t('permissions.read')}:{' '}
                                {selectedHasReadPermission ? t('common.yes') : t('common.no')}
                              </Badge>
                              <Badge
                                variant={
                                  selectedHasUpdatePermission ? 'outline-solid' : 'secondary'
                                }
                              >
                                {t('permissions.update')}:{' '}
                                {selectedHasUpdatePermission ? t('common.yes') : t('common.no')}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <div className="mt-0.5 flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            2
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">{t('permissions.stepScopes')}</div>
                            {renderScopeBadges(selectedScopes)}
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <div className="mt-0.5 flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            3
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              {t('permissions.stepExplicitApproval')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {selectedSupportsExplicitApproval
                                ? t('permissions.explicitApprovalHint')
                                : t('permissions.explicitNotApplicable')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="space-y-4">
                      <h4 className="text-sm font-semibold">{t('permissions.accessSources')}</h4>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.read')}
                          </div>
                          {selectedOverview ? (
                            renderSourceBadges(selectedReadAccess.sources)
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {t('permissions.accessLoading')}
                            </span>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.update')}
                          </div>
                          {selectedOverview ? (
                            renderSourceBadges(selectedUpdateAccess.sources)
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {t('permissions.accessLoading')}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              <Card>
                <CardContent className="space-y-4">
                  <h4 className="text-sm font-semibold">
                    {t('permissions.explicitApprovalTitle')}
                  </h4>
                  {selectedSupportsExplicitApproval ? (
                    <div className="space-y-3">
                      {selectedExplicitReadMessage ? (
                        <div className="text-xs text-muted-foreground">
                          {selectedExplicitReadMessage}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <Checkbox
                            id={selectedReadId}
                            checked={selectedReadChecked}
                            onCheckedChange={(checked) =>
                              void handleSelectedReadChange(Boolean(checked))
                            }
                            disabled={!selectedCanRead || isSaving}
                          />
                          <Label htmlFor={selectedReadId}>{t('permissions.read')}</Label>
                        </div>
                      )}
                      {selectedExplicitUpdateMessage ? (
                        <div className="text-xs text-muted-foreground">
                          {selectedExplicitUpdateMessage}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <Checkbox
                            id={selectedUpdateId}
                            checked={selectedUpdateChecked}
                            onCheckedChange={(checked) =>
                              void handleSelectedUpdateChange(Boolean(checked))
                            }
                            disabled={!selectedCanUpdate || isSaving}
                          />
                          <Label htmlFor={selectedUpdateId}>{t('permissions.update')}</Label>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      {t('permissions.explicitNotApplicable')}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="mt-6 text-sm text-muted-foreground">
              {t('permissions.detailsEmpty')}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
